import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowDownLeft, ArrowUpRight, Check, ChevronDown, Copy, Loader2, PhoneCall, PhoneOutgoing, Plus, RefreshCw, Save, Search, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components_dashboard/ui/alert-dialog';
import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components_dashboard/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components_dashboard/ui/select';
import { Switch } from '../components_dashboard/ui/switch';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const NEW_DID_NUMBER_ID = '__new__';

const EMPTY_DID_DRAFT = {
  id: '',
  number: '',
  provider: '',
  provider_id: '',
  channel: 'sip',
  direction: 'outbound',
  client_id: '',
  sip_trunk_id: '',
  carrier_trunk_id: '',
  sip_dispatch_rule_id: '',
  status: 'inactive',
  livekit_agent_name: '',
  call_limit: '',
  attach_group_ids: [],
  attach_group_ids_retry: [],
  __isNew: true,
};

export default function DidNumbersDashboard() {
  const { token } = useAuth();
  const [didNumbers, setDidNumbers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [clientGroups, setClientGroups] = useState([]);
  const [selectedDidNumberId, setSelectedDidNumberId] = useState('');
  const [draft, setDraft] = useState(null);
  const [loadingDidNumbers, setLoadingDidNumbers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredDidNumbers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return didNumbers;
    return didNumbers.filter((entry) =>
      String(entry.number || '').toLowerCase().includes(query)
      || String(entry.provider || '').toLowerCase().includes(query)
      || String(entry.sip_trunk_id || '').toLowerCase().includes(query)
      || String(entry.direction || '').toLowerCase().includes(query)
      || String(entry.client_id || '').toLowerCase().includes(query)
      || String(entry.status || '').toLowerCase().includes(query)
      || String(entry.livekit_agent_name || '').toLowerCase().includes(query)
    );
  }, [didNumbers, searchQuery]);

  const selectedDidNumber = useMemo(() => (
    didNumbers.find((entry) => entry.id === selectedDidNumberId) || null
  ), [didNumbers, selectedDidNumberId]);

  const providerNameById = useMemo(() => (
    Object.fromEntries(providers.map((entry) => [entry.id, entry.name]))
  ), [providers]);

  const resolveProviderName = (entry) => (
    providerNameById[entry?.provider_id] || entry?.provider || ''
  );

  const selectedAccessGroups = useMemo(() => (
    clientGroups.filter((group) => (draft?.attach_group_ids || []).includes(group.id))
  ), [clientGroups, draft]);

  const selectedRetryGroups = useMemo(() => (
    clientGroups.filter((group) => (draft?.attach_group_ids_retry || []).includes(group.id))
  ), [clientGroups, draft]);

  const isCreatingDidNumber = Boolean(draft?.__isNew);
  const isDraftActive = String(draft?.status || '').toLowerCase() === 'active';
  const draftProviderId = draft?.provider_id
    || providers.find((entry) => entry.name && entry.name === draft?.provider)?.id
    || '';
  // Anything that is not websocket dials over a trunk, which matches how the
  // backend reads a missing channel on DIDs created before this field existed.
  const isWebsocketChannel = String(draft?.channel || '').toLowerCase() === 'websocket';
  // The stored link, not the draft's: an unsaved pick can still be undone.
  const savedCarrierTrunkId = didNumbers.find((entry) => entry.id === selectedDidNumberId)?.carrier_trunk_id || '';

  const fetchDidNumbers = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingDidNumbers(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/did-numbers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(response.data?.did_numbers) ? response.data.did_numbers : [];
      setDidNumbers(rows);
      setSelectedDidNumberId((current) => {
        if (current === NEW_DID_NUMBER_ID) return current;
        if (current && rows.some((entry) => entry.id === current)) return current;
        return rows[0]?.id || '';
      });
    } catch (error) {
      console.error('Failed to fetch DID numbers', error);
      toast.error(error.response?.data?.detail || 'Failed to load DID numbers.');
      setDidNumbers([]);
      setSelectedDidNumberId('');
    } finally {
      setLoadingDidNumbers(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDidNumbers();
  }, [fetchDidNumbers]);

  useEffect(() => {
    if (!token) return;
    const fetchProviders = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/v1/admin/providers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProviders(Array.isArray(response.data?.providers) ? response.data.providers : []);
      } catch (error) {
        console.error('Failed to fetch providers', error);
        setProviders([]);
      }
    };
    fetchProviders();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${apiUrl}/api/v1/admin/carrier-trunks`, { headers: { Authorization: `Bearer ${token}` } })
      // Only a carrier with outbound settings can give a DID a trunk.
      .then((response) => setCarriers((response.data?.carrier_trunks || []).filter((carrier) => carrier.outbound)))
      .catch((error) => {
        console.error('Failed to fetch carrier trunks', error);
        setCarriers([]);
      });
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const fetchClientGroups = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/v1/admin/client-groups`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClientGroups(Array.isArray(response.data?.client_groups) ? response.data.client_groups : []);
      } catch (error) {
        console.error('Failed to fetch client groups', error);
        setClientGroups([]);
      }
    };
    fetchClientGroups();
  }, [token]);

  useEffect(() => {
    if (selectedDidNumberId === NEW_DID_NUMBER_ID) {
      setDraft({ ...EMPTY_DID_DRAFT });
    } else if (selectedDidNumber) {
      setDraft({ ...selectedDidNumber, __isNew: false });
    } else {
      setDraft(null);
    }
  }, [selectedDidNumber, selectedDidNumberId]);

  const handleCreateDraft = () => {
    setSelectedDidNumberId(NEW_DID_NUMBER_ID);
  };

  const handleFieldChange = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const handleGroupToggle = (field, groupId, checked) => {
    setDraft((current) => {
      const existing = new Set(current?.[field] || []);
      if (checked) {
        existing.add(groupId);
      } else {
        existing.delete(groupId);
      }
      return { ...current, [field]: Array.from(existing) };
    });
  };

  const applySavedDidNumber = (saved, isNewRecord) => {
    setDraft({ ...saved, __isNew: false });
    setSelectedDidNumberId(saved.id);
    setDidNumbers((current) => (
      isNewRecord
        ? [saved, ...current]
        : current.map((entry) => (entry.id === saved.id ? saved : entry))
    ));
  };

  const handleSave = async () => {
    if (!draft) return;

    if (!String(draft.number || '').trim()) {
      toast.error('Number is required.');
      return;
    }

    if (!isWebsocketChannel && !draft.carrier_trunk_id && !String(draft.sip_trunk_id || '').trim()) {
      toast.error('Pick a carrier trunk or enter a SIP Trunk ID when the channel is SIP.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        number: draft.number || null,
        provider_id: draftProviderId || null,
        channel: isWebsocketChannel ? 'websocket' : 'sip',
        direction: draft.direction || 'outbound',
        // A new linked DID gets its trunk from sip-control, never a hidden typed one.
        sip_trunk_id: isWebsocketChannel || (isCreatingDidNumber && draft.carrier_trunk_id) ? null : (draft.sip_trunk_id || null),
        carrier_trunk_id: isWebsocketChannel ? null : (draft.carrier_trunk_id || null),
        status: draft.status || null,
        livekit_agent_name: draft.livekit_agent_name || null,
        call_limit: draft.call_limit === '' || draft.call_limit === null || draft.call_limit === undefined
          ? null
          : Number(draft.call_limit),
        attach_group_ids: draft.attach_group_ids || [],
        attach_group_ids_retry: draft.attach_group_ids_retry || [],
      };

      const response = isCreatingDidNumber
        ? await axios.post(`${apiUrl}/api/v1/admin/did-numbers`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        : await axios.patch(`${apiUrl}/api/v1/admin/did-numbers/${selectedDidNumberId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

      const updatedDidNumber = response.data;
      applySavedDidNumber(updatedDidNumber, isCreatingDidNumber);
      toast.success(isCreatingDidNumber ? 'DID number created.' : 'DID number updated.');
    } catch (error) {
      console.error('Failed to save DID number', error);
      toast.error(error.response?.data?.detail || 'Failed to save DID number.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDidNumberId || selectedDidNumberId === NEW_DID_NUMBER_ID) return;
    const deletedId = selectedDidNumberId;
    try {
      setDeleting(true);
      await axios.delete(`${apiUrl}/api/v1/admin/did-numbers/${deletedId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const remaining = didNumbers.filter((entry) => entry.id !== deletedId);
      setDidNumbers(remaining);
      setSelectedDidNumberId(remaining[0]?.id || '');
      setConfirmDeleteOpen(false);
      toast.success('DID number deleted.');
    } catch (error) {
      console.error('Failed to delete DID number', error);
      toast.error(error.response?.data?.detail || 'Failed to delete DID number.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <main className="flex-1 flex flex-col min-w-0 p-6 lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                <PhoneCall className="w-3.5 h-3.5" />
                DID Controls
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">DID Number Manager</h1>
              <p className="mt-1 text-slate-500">Browse DID numbers from the `did_numbers` collection and inspect trunk and provider details.</p>
            </div>

            <Button
              variant="outline"
              onClick={fetchDidNumbers}
              disabled={loadingDidNumbers}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              {loadingDidNumbers ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh DID Numbers
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100">
                <div className="space-y-3">
                  <Button
                    onClick={handleCreateDraft}
                    className="w-full text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create DID Number
                  </Button>
                  <div className="relative">
                    <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search DID numbers..."
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <div className="max-h-[72vh] overflow-y-auto">
                {loadingDidNumbers ? (
                  <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading DID numbers...
                  </div>
                ) : filteredDidNumbers.length === 0 ? (
                  <div className="px-4 py-10 text-sm text-center text-slate-500">
                    No DID numbers match the current search.
                  </div>
                ) : (
                  filteredDidNumbers.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => setSelectedDidNumberId(entry.id)}
                      className={`flex w-full items-start gap-3 border-b border-slate-100 px-4 py-4 text-left transition-colors last:border-b-0 ${
                        selectedDidNumberId === entry.id ? 'bg-violet-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-violet-100 text-violet-700">
                        <PhoneCall className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold truncate text-slate-900">{entry.number || 'Unnamed DID Number'}</div>
                        </div>
                        <div className="text-sm truncate text-slate-500">{resolveProviderName(entry) ? `Provider: ${resolveProviderName(entry)}` : 'No provider'}</div>
                        <div className="mt-1 text-sm truncate text-slate-400">
                          {entry.livekit_agent_name ? `LiveKit Agent: ${entry.livekit_agent_name}` : 'No LiveKit agent'}
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          <span className={`rounded-full px-2 py-0.5 font-medium ${
                            entry.direction === 'inbound'
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : entry.direction === 'both'
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : 'bg-slate-100 text-slate-700'
                          }`}>
                            {entry.direction === 'inbound' ? 'Inbound' : entry.direction === 'both' ? 'Both' : 'Outbound'}
                          </span>
                          <span className="rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700">
                            {String(entry.channel || '').toLowerCase() === 'websocket' ? 'WebSocket' : 'SIP'}
                          </span>
                          {String(entry.channel || '').toLowerCase() !== 'websocket' ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                              {entry.sip_trunk_id || 'No SIP trunk'}
                            </span>
                          ) : null}
                          <span className={`rounded-full px-2 py-0.5 font-medium ${
                            String(entry.status || '').toLowerCase() === 'active'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-red-50 text-red-700'
                          }`}
                          >
                            {String(entry.status || '').toLowerCase() === 'active' ? 'Active' : 'Inactive'}
                          </span>
                          {typeof entry.call_limit === 'number' ? (
                            <span className={`rounded-full px-2 py-0.5 font-medium ${
                              entry.call_limit <= 0
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                            >
                              {entry.call_limit <= 0 ? 'Limit reached' : `${entry.call_limit} left`}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              {loadingDidNumbers ? (
                <div className="flex min-h-[520px] items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading DID number details...
                </div>
              ) : !draft ? (
                <div className="flex min-h-[520px] items-center justify-center gap-2 text-sm text-slate-500">
                  Select a DID number to inspect.
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  <div className="flex flex-col gap-4 pb-6 border-b border-slate-100 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">{draft.number || 'Create DID Number'}</h2>
                      <p className="mt-1 text-sm text-slate-500">{resolveProviderName(draft) ? `Provider: ${resolveProviderName(draft)}` : 'Fill the DID details and save to create a new record.'}</p>
                      {!isCreatingDidNumber ? (
                        <div className="flex flex-wrap gap-2 mt-3 text-xs">
                          <span className="px-3 py-1 font-medium rounded-full bg-slate-100 text-slate-600">ID: {draft.id}</span>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {!isCreatingDidNumber ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setConfirmDeleteOpen(true)}
                          disabled={saving || deleting}
                          className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                          Delete
                        </Button>
                      ) : null}
                      <Button
                        onClick={handleSave}
                        disabled={saving || deleting}
                        className="text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        {isCreatingDidNumber ? 'Create DID Number' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>

                  <AlertDialog open={confirmDeleteOpen} onOpenChange={(open) => { if (!deleting) setConfirmDeleteOpen(open); }}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this DID number?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {`This will permanently delete "${draft.number || 'this DID number'}". This action cannot be undone, and the number will no longer be available for scheduling or the dialer.`}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>No, cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={(event) => { event.preventDefault(); handleDelete(); }}
                          disabled={deleting}
                          className="text-white bg-red-600 hover:bg-red-700 focus:ring-red-600"
                        >
                          {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                          Yes, delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  {/* Direction & Basic Telephony Settings */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>DID Number *</Label>
                      <Input
                        placeholder="+91..."
                        value={draft.number || ''}
                        disabled={!isCreatingDidNumber}
                        onChange={(event) => handleFieldChange('number', event.target.value)}
                      />
                      {!isCreatingDidNumber && (
                        <p className="text-xs text-slate-500">
                          DID number cannot be changed after creation.
                        </p>
                      )}
                    </div>
                                        <div className="space-y-2">
                      <Label>Call Direction *</Label>
                      <Select
                        value={draft.direction || 'outbound'}
                        onValueChange={(value) => handleFieldChange('direction', value)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select direction" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="outbound">Outbound Only</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Dedicated for outbound dialer, batch campaigns, and retry pools.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Provider</Label>
                      <Select
                        value={draftProviderId || '__none__'}
                        onValueChange={(value) => handleFieldChange('provider_id', value === '__none__' ? '' : value)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select a provider" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No provider selected</SelectItem>
                          {providers.map((provider) => (
                            <SelectItem key={provider.id} value={provider.id}>{provider.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Channel</Label>
                      <div className="flex items-center gap-3 h-9">
                        <span className={!isWebsocketChannel ? 'text-sm font-medium text-slate-900' : 'text-sm text-slate-400'}>SIP</span>
                        <Switch
                          checked={isWebsocketChannel}
                          disabled={!isCreatingDidNumber}
                          onCheckedChange={(checked) => handleFieldChange('channel', checked ? 'websocket' : 'sip')}
                        />
                        <span className={isWebsocketChannel ? 'text-sm font-medium text-slate-900' : 'text-sm text-slate-400'}>WebSocket</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {isCreatingDidNumber
                          ? 'SIP dials over a trunk. WebSocket hands the call to the provider API.'
                          : 'Channel cannot be changed after creation.'}
                      </p>
                    </div>

                    {!isWebsocketChannel ? (
                      <div className="space-y-2">
                        <Label htmlFor="did-carrier-trunk">Carrier Trunk</Label>
                        <select
                          id="did-carrier-trunk"
                          className="w-full h-9 px-3 text-sm rounded-md border border-slate-200 bg-white"
                          value={draft.carrier_trunk_id || ''}
                          onChange={(event) => handleFieldChange('carrier_trunk_id', event.target.value)}
                        >
                          {/* Unlinking would strand the DID on a trunk sip-control deletes. */}
                          <option value="" disabled={Boolean(savedCarrierTrunkId)}>None (type a trunk ID)</option>
                          {carriers.map((carrier) => (
                            <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-500">
                          {draft.carrier_trunk_id
                            ? 'LiveKit trunk is created from the carrier within a minute. A new number starts inactive; activate it once the trunk is live.'
                            : 'Linking to a carrier lets the system create and maintain this number\'s LiveKit trunk.'}
                        </p>
                      </div>
                    ) : null}

                    {!isWebsocketChannel && draft.carrier_trunk_id ? (
                      <div className="space-y-2">
                        <Label>SIP Trunk ID</Label>
                        <div className="flex items-center gap-2 h-9 text-sm font-mono text-slate-600">
                          {draft.sip_trunk_id || 'pending'}
                          {draft.sip_trunk_status ? (
                            <span className={`rounded-full px-2 py-0.5 text-xs font-sans font-medium ${
                              draft.sip_trunk_status === 'live' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {draft.sip_trunk_status}
                            </span>
                          ) : null}
                        </div>
                        {draft.sip_trunk_detail ? <p className="text-xs text-red-600">{draft.sip_trunk_detail}</p> : null}
                      </div>
                    ) : null}

                    {!isWebsocketChannel && !draft.carrier_trunk_id ? (
                      <div className="space-y-2">
                        <Label>SIP Trunk ID *</Label>
                        <Input
                          placeholder="ST_..."
                          value={draft.sip_trunk_id || ''}
                          onChange={(event) => handleFieldChange('sip_trunk_id', event.target.value)}
                        />
                        <p className="text-xs text-slate-500">
                          Enter the SIP trunk identifier for outbound dialing.
                        </p>
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <Label>LiveKit Worker Agent Name</Label>
                      <Input
                        placeholder="Leave empty for system default (my-telephony-agent)"
                        value={draft.livekit_agent_name || ''}
                        onChange={(event) => handleFieldChange('livekit_agent_name', event.target.value)}
                      />
                      <p className="text-xs text-slate-500">
                        Defaults to configured worker from environment.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Call Limit</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="Leave empty for no limit"
                        value={draft.call_limit ?? ''}
                        onChange={(event) => handleFieldChange('call_limit', event.target.value)}
                      />
                      <p className="text-xs text-slate-500">
                        Calls remaining on this DID before rotation.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Activation Status</h3>
                      <p className="text-sm text-slate-500">Toggle this to set the DID number as active or inactive.</p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                      <div className="p-4 border rounded-xl border-slate-200">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-medium text-slate-900">{isDraftActive ? 'Active' : 'Inactive'}</div>
                            <p className="mt-1 text-sm text-slate-500">
                              {isDraftActive
                                ? 'This DID number is currently available for active use.'
                                : 'This DID number is currently disabled.'}
                            </p>
                          </div>
                          <Switch
                            checked={isDraftActive}
                            onCheckedChange={(checked) => handleFieldChange('status', checked ? 'active' : 'inactive')}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Account Access</h3>
                      <p className="text-sm text-slate-500">
                        Attach this number to a client group instead of granting it one user at a time.
                      </p>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">Attach to client groups</h4>
                          <p className="mt-1 text-sm text-slate-500">
                            Grants this DID number to every account in the selected groups (current and future members).
                          </p>
                        </div>
                        <div className="w-full lg:w-[380px]">
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-violet-200 bg-white px-3 py-2 text-left text-sm shadow-sm transition hover:border-violet-300"
                              >
                                <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                                  {selectedAccessGroups.length ? (
                                    selectedAccessGroups.map((group) => (
                                      <span
                                        key={group.id}
                                        className="inline-flex max-w-full items-center gap-1 rounded-md bg-violet-50 px-2 py-1 text-xs font-medium text-slate-700"
                                      >
                                        <span className="truncate">{group.name}</span>
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-slate-400">Select client groups</span>
                                  )}
                                </div>
                                <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-[380px] max-w-[calc(100vw-3rem)] p-2">
                              <div className="max-h-72 space-y-1 overflow-y-auto">
                                {clientGroups.length === 0 ? (
                                  <div className="px-2 py-6 text-sm text-center text-slate-500">
                                    No client groups yet — create one from the Client Groups page.
                                  </div>
                                ) : (
                                  clientGroups.map((group) => {
                                    const isChecked = (draft.attach_group_ids || []).includes(group.id);
                                    return (
                                      <div
                                        key={group.id}
                                        role="button"
                                        tabIndex={0}
                                        className="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-violet-50"
                                        onClick={() => handleGroupToggle('attach_group_ids', group.id, !isChecked)}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            handleGroupToggle('attach_group_ids', group.id, !isChecked);
                                          }
                                        }}
                                      >
                                        <span
                                          className="mt-1 flex shrink-0 items-center justify-center"
                                          style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '4px',
                                            border: isChecked ? '2px solid #7c3aed' : '2px solid #000000',
                                            backgroundColor: isChecked ? '#7c3aed' : '#ffffff',
                                          }}
                                        >
                                          {isChecked && <Check className="h-3 w-3" style={{ color: '#ffffff' }} />}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                          <span className="font-medium break-all text-slate-900">{group.name}</span>
                                          <p className="mt-0.5 text-xs break-all text-slate-500">
                                            {(group.user_ids || []).length} account{(group.user_ids || []).length === 1 ? '' : 's'}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">Attach to client groups as retry</h4>
                          <p className="mt-1 text-sm text-slate-500">
                            Adds this DID number to the retry list of every account in the selected groups.
                          </p>
                        </div>
                        <div className="w-full lg:w-[380px]">
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-violet-200 bg-white px-3 py-2 text-left text-sm shadow-sm transition hover:border-violet-300"
                              >
                                <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                                  {selectedRetryGroups.length ? (
                                    selectedRetryGroups.map((group) => (
                                      <span
                                        key={group.id}
                                        className="inline-flex max-w-full items-center gap-1 rounded-md bg-violet-50 px-2 py-1 text-xs font-medium text-slate-700"
                                      >
                                        <span className="truncate">{group.name}</span>
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-slate-400">Select client groups</span>
                                  )}
                                </div>
                                <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-[380px] max-w-[calc(100vw-3rem)] p-2">
                              <div className="max-h-72 space-y-1 overflow-y-auto">
                                {clientGroups.length === 0 ? (
                                  <div className="px-2 py-6 text-sm text-center text-slate-500">
                                    No client groups yet — create one from the Client Groups page.
                                  </div>
                                ) : (
                                  clientGroups.map((group) => {
                                    const isChecked = (draft.attach_group_ids_retry || []).includes(group.id);
                                    return (
                                      <div
                                        key={group.id}
                                        role="button"
                                        tabIndex={0}
                                        className="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-violet-50"
                                        onClick={() => handleGroupToggle('attach_group_ids_retry', group.id, !isChecked)}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            handleGroupToggle('attach_group_ids_retry', group.id, !isChecked);
                                          }
                                        }}
                                      >
                                        <span
                                          className="mt-1 flex shrink-0 items-center justify-center"
                                          style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '4px',
                                            border: isChecked ? '2px solid #7c3aed' : '2px solid #000000',
                                            backgroundColor: isChecked ? '#7c3aed' : '#ffffff',
                                          }}
                                        >
                                          {isChecked && <Check className="h-3 w-3" style={{ color: '#ffffff' }} />}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                          <span className="font-medium break-all text-slate-900">{group.name}</span>
                                          <p className="mt-0.5 text-xs break-all text-slate-500">
                                            {(group.user_ids || []).length} account{(group.user_ids || []).length === 1 ? '' : 's'}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
